import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import clsx from "clsx";
import { Paper, makeStyles, IconButton, InputBase } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import ContactDrawer from "../ContactDrawer";
import ContactOverviewDrawer from "../ContactOverviewDrawer";
import ModoMissao from "../ModoMissao";
import OpportunityModal from "../OpportunityModal";
import ConversationLeadCRM from "../ConversationLeadCRM";
import MessageInput from "../MessageInput";
import TicketHeader from "../TicketHeader";
import TicketIABar from "../ticketV3/TicketIABar";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from "lodash";
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const drawerWidth = 320;
const missionWidth = 300;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#F5F7FB",
  },
  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#FFFFFF",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  mainWrapperShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  missionDrawer: {
    position: "absolute",
    right: 0, top: 0, bottom: 0,
    width: missionWidth,
    zIndex: 1600,
    boxShadow: "-12px 0 34px rgba(15,23,42,.12)",
    borderLeft: "1px solid #E2E8F0",
    background: "#FFFFFF",
  },
  // Header info row — estilo WhatsApp
  headerRow: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    gap: 8,
    overflow: "hidden",
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    minWidth: 0,
    gap: 6,
    [theme.breakpoints.down("sm")]: {
      gap: 4,
    },
  },
  desktopActions: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  headerTagsButton: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  mobileOnly: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
    },
  },
  koraxMenuOverlay: {
    position: "absolute",
    right: 12,
    bottom: 132,
    zIndex: 1700,
    width: 260,
    borderRadius: 22,
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    boxShadow: "0 24px 60px rgba(15,23,42,.18)",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      right: 10,
      bottom: 132,
      width: "calc(100% - 20px)",
      maxWidth: 340,
    },
  },
  koraxMenuHeader: {
    padding: "14px 16px",
    background: "linear-gradient(135deg, #0F1B35, #1D4ED8)",
    color: "#FFFFFF",
  },
  koraxMenuItem: {
    width: "100%",
    border: "none",
    background: "#FFFFFF",
    padding: "13px 15px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    textAlign: "left",
    borderBottom: "1px solid #EEF2F7",
    color: "#0F172A",
    fontWeight: 800,
    fontSize: 13,
    "&:hover": {
      background: "#F8FAFC",
    },
  },
  koraxMenuIcon: {
    width: 36,
    height: 36,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 17,
  },
  // Tags dropdown
  tagsDropdown: {
    position: "absolute",
    top: 56,
    left: 8,
    right: 8,
    zIndex: 1700,
    background: "#fff",
    borderRadius: 12,
    padding: 14,
    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
    border: "1px solid #E2E8F0",
  },
  // Painel de mensagens favoritas
  favoritesPanel: {
    position: "absolute",
    top: 58,
    right: 10,
    zIndex: 1750,
    width: 370,
    maxWidth: "calc(100% - 20px)",
    maxHeight: "calc(100% - 120px)",
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 16,
    boxShadow: "0 20px 55px rgba(15,23,42,.18)",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      left: 10,
      right: 10,
      width: "auto",
      maxWidth: "none",
      top: 58
    }
  },

  favoritesPanelHeader: {
    minHeight: 58,
    padding: "12px 14px 12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid #E2E8F0",
    background: "#FFFFFF"
  },

  favoritesPanelTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 900
  },

  favoritesPanelCount: {
    minWidth: 22,
    height: 22,
    padding: "0 7px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#FFF7ED",
    color: "#B45309",
    fontSize: 11,
    fontWeight: 900
  },

  favoritesPanelBody: {
    overflowY: "auto",
    padding: 8,
    ...theme.scrollbarStyles
  },

  favoriteMessageItem: {
    width: "100%",
    display: "block",
    border: "none",
    borderBottom: "1px solid #F1F5F9",
    borderRadius: 10,
    background: "#FFFFFF",
    padding: "11px 12px",
    textAlign: "left",
    cursor: "pointer",

    "&:hover": {
      background: "#F8FAFC"
    }
  },

  favoriteMessageMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 5,
    fontSize: 11,
    color: "#64748B"
  },

  favoriteMessageSender: {
    fontWeight: 800,
    color: "#334155",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  favoriteMessageBody: {
    fontSize: 13,
    lineHeight: 1.45,
    color: "#0F172A",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 3
  },

  favoriteEmpty: {
    padding: "28px 18px",
    textAlign: "center",
    color: "#64748B",
    fontSize: 13
  },

  // Botoes flutuantes
  fabGroup: {
    position: "absolute",
    right: 14,
    bottom: 74,
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    gap: 9,
    alignItems: "center",
  },
  fab: {
    width: 46,
    height: 46,
    borderRadius: 16,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.08rem",
    transition: "all 0.2s ease",
    boxShadow: "0 12px 28px rgba(15,23,42,.14)",
    [theme.breakpoints.down("sm")]: {
      width: 48,
      height: 48,
      borderRadius: 18,
      fontSize: "1rem",
    },
  },
  resumeIaMobileOnly: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      margin: "8px 10px",
      borderRadius: 18,
      boxShadow: "0 10px 26px rgba(0,102,255,.12)"
    }
  },

  iaAcceptBanner: {
    flex: "none",
    margin: "0 12px 10px",
    padding: "12px 14px",
    borderRadius: 16,
    background: "linear-gradient(135deg, #EFF6FF, #FFFFFF)",
    border: "1px solid rgba(37,99,235,.18)",
    boxShadow: "0 10px 26px rgba(37,99,235,.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    zIndex: 950,
    [theme.breakpoints.down("sm")]: {
      margin: "0 8px 8px",
      padding: "10px 11px",
      borderRadius: 14,
    },
  },
  iaAcceptTitle: {
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 13,
  },
  iaAcceptText: {
    color: "#64748B",
    fontSize: 12,
    marginTop: 2,
  },
  iaAcceptBtn: {
    border: "none",
    borderRadius: 999,
    padding: "10px 14px",
    background: "linear-gradient(135deg,#2563EB,#1D4ED8)",
    color: "#FFFFFF",
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: "0 10px 22px rgba(37,99,235,.20)",
    [theme.breakpoints.down("sm")]: {
      padding: "9px 12px",
      fontSize: 12,
    },
  },

  todayAppointmentBanner: {
    flex: "none",
    margin: "6px 12px 0",
    padding: "7px 10px",
    borderRadius: 12,
    background: "#F0F7FF",
    border: "1px solid #BFDBFE",
    [theme.breakpoints.down("sm")]: {
      margin: "6px 8px 0",
      padding: "7px 9px",
    },
  },

  todayAppointmentHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#1D4ED8",
    fontSize: 11,
    fontWeight: 900,
    marginBottom: 3,
  },

  todayAppointmentItem: {
    display: "grid",
    gridTemplateColumns:
      "58px minmax(0, 1fr) minmax(0, .8fr) auto",
    alignItems: "center",
    gap: 8,
    minHeight: 32,
    "& + &": {
      borderTop: "1px solid rgba(59,130,246,.14)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns:
        "48px minmax(0, 1fr) auto",
      gap: 5,
    },
  },

  todayAppointmentTime: {
    color: "#1D4ED8",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  todayAppointmentMain: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#1E293B",
    fontSize: 11.5,
    fontWeight: 700,
  },

  todayAppointmentMeta: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#64748B",
    fontSize: 10.5,
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  todayAppointmentComplete: {
    flexShrink: 0,
    border: "1px solid #BBF7D0",
    background: "#F0FDF4",
    color: "#15803D",
    fontSize: 10.5,
    fontWeight: 900,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 8,
    whiteSpace: "nowrap",
    "&:hover": {
      background: "#DCFCE7",
    },
    "&:disabled": {
      opacity: .45,
      cursor: "default",
    },
  },

  followUpBanner: {
    flex: "none",
    margin: "6px 12px 0",
    padding: "7px 10px",
    borderRadius: 12,
    background: "#FFF9F2",
    border: "1px solid #FED7AA",
    [theme.breakpoints.down("sm")]: {
      margin: "6px 8px 0",
      padding: "7px 9px",
    },
  },

  followUpBannerHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#9A3412",
    fontSize: 11,
    fontWeight: 900,
    marginBottom: 3,
  },

  followUpItem: {
    display: "grid",
    gridTemplateColumns: "88px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 8,
    minHeight: 30,
    "& + &": {
      borderTop: "1px solid rgba(251,146,60,.18)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "78px minmax(0, 1fr) auto",
      gap: 5,
    },
  },

  followUpDate: {
    color: "#9A3412",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  followUpPreview: {
    minWidth: 0,
    color: "#475569",
    fontSize: 11.5,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  followUpCancel: {
    flexShrink: 0,
    border: "none",
    background: "transparent",
    color: "#DC2626",
    fontSize: 10.5,
    fontWeight: 900,
    cursor: "pointer",
    padding: "4px 6px",
    borderRadius: 7,
    "&:hover": {
      background: "rgba(220,38,38,.07)",
    },
    "&:disabled": {
      opacity: .45,
      cursor: "default",
    },
  },

  searchBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderBottom: "1px solid #E2E8F0",
    background: "#F8FAFC",
  },
  softActionBtn: {
    minWidth: 36,
    height: 36,
    borderRadius: 13,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    color: "#2563EB",
    cursor: "pointer",
    fontWeight: 900,
    boxShadow: "0 6px 16px rgba(15,23,42,.05)",
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [favoritePanelOpen, setFavoritePanelOpen] = useState(false);
  const [favoriteMessages, setFavoriteMessages] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoriteRefreshKey, setFavoriteRefreshKey] = useState(0);
  const [favoriteJumpRequest, setFavoriteJumpRequest] = useState(null);
  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);
  const { companyId } = user;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [legacyContactDrawerOpen, setLegacyContactDrawerOpen] = useState(false);
  const [missionOpen, setMissionOpen] = useState(false);
  const [seqOpen, setSeqOpen] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [firingSeq, setFiringSeq] = useState(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
  const [leadCrmOpen, setLeadCrmOpen] = useState(false);
  const [koraxActionsOpen, setKoraxActionsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const [messageSearch, setMessageSearch] = useState("");
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const [todayAppointments, setTodayAppointments] = useState([]);
  const [finishingAppointmentId, setFinishingAppointmentId] =
    useState(null);

  const [pendingFollowUps, setPendingFollowUps] = useState([]);
  const [cancellingFollowUpId, setCancellingFollowUpId] = useState(null);

  const loadTodayAppointments = useCallback(async () => {
    const contactId =
      ticket?.contactId ||
      ticket?.contact?.id ||
      contact?.id;

    if (!contactId) {
      setTodayAppointments([]);
      return;
    }

    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setHours(23, 59, 59, 999);

    try {
      const { data } = await api.get("/appointments", {
        params: {
          contactId,
          startDate: start.toISOString(),
          endDate: end.toISOString()
        }
      });

      const activeStatuses = [
        "agendado",
        "confirmado",
        "em_atendimento"
      ];

      const appointments = Array.isArray(data)
        ? data
        : [];

      setTodayAppointments(
        appointments
          .filter(item =>
            activeStatuses.includes(item.status)
          )
          .sort(
            (a, b) =>
              new Date(a.startTime).getTime() -
              new Date(b.startTime).getTime()
          )
      );
    } catch (err) {
      console.error(
        "Erro ao carregar agendamentos de hoje",
        err
      );

      setTodayAppointments([]);
    }
  }, [
    ticket?.contactId,
    ticket?.contact?.id,
    contact?.id
  ]);

  const formatAppointmentTime = value => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleTimeString(
      "pt-BR",
      {
        hour: "2-digit",
        minute: "2-digit"
      }
    );
  };

  const handleFinishAppointment = async appointment => {
    if (!appointment?.id) return;

    const confirmed = window.confirm(
      "Marcar este atendimento como finalizado?"
    );

    if (!confirmed) return;

    try {
      setFinishingAppointmentId(
        appointment.id
      );

      await api.put(
        `/appointments/${appointment.id}`,
        {
          status: "finalizado"
        }
      );

      setTodayAppointments(current =>
        current.filter(
          item =>
            Number(item.id) !==
            Number(appointment.id)
        )
      );

      toast.success(
        "Atendimento finalizado."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setFinishingAppointmentId(null);
    }
  };

  const loadPendingFollowUps = useCallback(async () => {
    if (!ticket?.id) {
      setPendingFollowUps([]);
      return;
    }

    try {
      const { data } = await api.get("/schedules", {
        params: {
          ticketId: ticket.id,
          pendingOnly: "true",
          pageNumber: 1
        }
      });

      const schedules = Array.isArray(data?.schedules)
        ? data.schedules
        : [];

      const sorted = [...schedules].sort(
        (a, b) =>
          new Date(a.sendAt).getTime() -
          new Date(b.sendAt).getTime()
      );

      setPendingFollowUps(sorted);
    } catch (err) {
      console.error(
        "Erro ao carregar follow-ups da conversa",
        err
      );

      setPendingFollowUps([]);
    }
  }, [ticket?.id]);

  const formatFollowUpDate = value => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleCancelFollowUp = async schedule => {
    if (!schedule?.id) return;

    const confirmed = window.confirm(
      "Cancelar este follow-up agendado?"
    );

    if (!confirmed) return;

    try {
      setCancellingFollowUpId(schedule.id);

      await api.delete(
        `/schedules/${schedule.id}`
      );

      setPendingFollowUps(current =>
        current.filter(
          item =>
            Number(item.id) !==
            Number(schedule.id)
        )
      );

      toast.success("Follow-up cancelado.");
    } catch (err) {
      toastError(err);
    } finally {
      setCancellingFollowUpId(null);
    }
  };

  // Protege a conversa contra eventos realtime intermediarios
  // enquanto o ticket passa de IA para atendimento humano.
  const keepOpenAfterAcceptRef = React.useRef(false);
  const acceptedUuidAfterAcceptRef = React.useRef(null);

  React.useEffect(() => {
    window.__setTicketMessages = setTicketMessages;
    return () => { window.__setTicketMessages = null; };
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        if (!isNil(ticketId) && ticketId !== "undefined") {
          const { data } = await api.get("/tickets/u/" + ticketId);
          setContact(data.contact);
          setTicket(data);
          if (["pending", "open", "group"].includes(data.status)) {
            setTabOpen(data.status);
          }
          setLoading(false);
        }
      } catch (err) {
        // Durante o aceite pode existir uma pequena janela em que
        // o UUID antigo deixa de responder antes do novo estabilizar.
        if (keepOpenAfterAcceptRef.current) {
          const keepUuid =
            acceptedUuidAfterAcceptRef.current ||
            ticketId;

          if (keepUuid) {
            history.replace(`/tickets/${keepUuid}`);
          }
        } else {
          history.push("/tickets");
        }

        setLoading(false);
        toastError(err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [ticketId, user, history]);

  useEffect(() => {
    loadTodayAppointments();
  }, [loadTodayAppointments]);

  useEffect(() => {
    loadPendingFollowUps();
  }, [loadPendingFollowUps]);

  useEffect(() => {
    if (!ticket?.id || !user.companyId) return;
    const onConnect = () => socket.emit("joinChatBox", `${ticket.id}`);
    const onTicket = (data) => {
      if (
        data.action === "update" &&
        data.ticket.id === ticket?.id
      ) {
        setTicket(data.ticket);
      }

      if (
        data.action === "delete" &&
        data.ticketId === ticket?.id
      ) {
        // Ao assumir uma conversa, o backend pode emitir eventos
        // intermediarios enquanto reorganiza/consolida o ticket.
        // Nessa janela preservamos a conversa aberta.
        if (keepOpenAfterAcceptRef.current) {
          const keepUuid =
            acceptedUuidAfterAcceptRef.current ||
            ticket?.uuid ||
            ticketId;

          if (keepUuid) {
            history.replace(`/tickets/${keepUuid}`);
          }

          return;
        }

        history.push("/tickets");
      }
    };
    const onContact = (data) => {
      if (data.action === "update") {
        setContact(prev => prev.id === data.contact?.id ? { ...prev, ...data.contact } : prev);
      }
    };
    socket.on("connect", onConnect);
    socket.on(`company-${companyId}-ticket`, onTicket);
    socket.on(`company-${companyId}-contact`, onContact);
    return () => {
      socket.emit("joinChatBoxLeave", `${ticket.id}`);
      socket.off("connect", onConnect);
      socket.off(`company-${companyId}-ticket`, onTicket);
      socket.off(`company-${companyId}-contact`, onContact);
    };
  }, [ticketId, ticket, history]);

  const handleToggleSearch = useCallback(() => {
    setShowMessageSearch(prev => { if (prev) setMessageSearch(""); return !prev; });
  }, []);

  const handleAcceptIaConversation = async () => {
    if (!ticket?.id) return;

    // A partir daqui a rota atual deve ser preservada
    // ate o backend/socket estabilizar a mudanca.
    keepOpenAfterAcceptRef.current = true;
    acceptedUuidAfterAcceptRef.current =
      ticket?.uuid || ticketId || null;

    try {
      const { data } = await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user?.id,
        aiActive: false,
        isTransfered: false
      });

      const acceptedTicket =
        data?.id
          ? data
          : {
              ...ticket,
              status: "open",
              userId: user?.id,
              aiActive: false,
              isTransfered: false
            };

      setTicket(acceptedTicket);

      if (acceptedTicket?.contact) {
        setContact(acceptedTicket.contact);
      }

      setTabOpen(
        acceptedTicket?.isGroup
          ? "group"
          : "open"
      );

      const acceptedUuid =
        acceptedTicket?.uuid ||
        ticket?.uuid ||
        ticketId;

      acceptedUuidAfterAcceptRef.current =
        acceptedUuid || null;

      // Mantemos explicitamente a rota do atendimento.
      // Mesmo se o UUID nao mudou, isso neutraliza qualquer
      // navegacao intermediaria disparada durante o aceite.
      if (acceptedUuid) {
        history.replace(
          `/tickets/${acceptedUuid}`
        );
      }

      toast.success("Conversa aceita com sucesso.");

      // O realtime normalmente estabiliza quase imediatamente.
      // Mantemos uma pequena janela de protecao contra eventos
      // atrasados referentes ao estado anterior do ticket.
      window.setTimeout(() => {
        keepOpenAfterAcceptRef.current = false;
        acceptedUuidAfterAcceptRef.current = null;
      }, 3000);

    } catch (err) {
      keepOpenAfterAcceptRef.current = false;
      acceptedUuidAfterAcceptRef.current = null;

      toastError(err);
    }
  };

  const handleResumeIaConversation = async () => {
    if (!ticket?.id) return;

    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "pending",
        userId: null,
        aiActive: true,
        aiPromptId: ticket.aiPromptId || null,
        isBot: true,
        amountUsedBotQueues: 0,
        queueId: ticket.queueId || null
      });

      const { data } = await api.get("/tickets/u/" + ticketId);
      setTicket(data);
      setContact(data.contact);
      toast.success("Conversa devolvida para a Korax IA.");
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    const handleKoraxOpenSequencesFromInput = () => {
      setSeqOpen(true);
      api.get("/message-sequences")
        .then(r => setSequences(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    };

    const handleKoraxOpenOpportunityFromInput = () => {
      setOpportunityModalOpen(true);
    };

    const handleKoraxOpenMissionFromInput = () => {
      setMissionOpen(true);
    };

    const handleKoraxOpenLeadDetailsFromInput = () => {
      setLeadCrmOpen(true);
    };

    const handleKoraxAnalyzeConversationFromInput = () => {
      setDrawerOpen(true);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("koraxTriggerAiSummary"));
      }, 450);
    };

    window.addEventListener("koraxOpenSequences", handleKoraxOpenSequencesFromInput);
    window.addEventListener("koraxAnalyzeConversation", handleKoraxAnalyzeConversationFromInput);
    window.addEventListener("koraxOpenOpportunity", handleKoraxOpenOpportunityFromInput);
    window.addEventListener("koraxOpenMission", handleKoraxOpenMissionFromInput);
    window.addEventListener("koraxOpenLeadDetails", handleKoraxOpenLeadDetailsFromInput);

    return () => {
      window.removeEventListener("koraxOpenSequences", handleKoraxOpenSequencesFromInput);
      window.removeEventListener("koraxAnalyzeConversation", handleKoraxAnalyzeConversationFromInput);
      window.removeEventListener("koraxOpenOpportunity", handleKoraxOpenOpportunityFromInput);
      window.removeEventListener("koraxOpenMission", handleKoraxOpenMissionFromInput);
      window.removeEventListener("koraxOpenLeadDetails", handleKoraxOpenLeadDetailsFromInput);
    };
  }, []);

  useEffect(() => {
    const activateConversation = async () => {
      if (!ticket?.id) return;
      try {
        await api.put(`/tickets/${ticket.id}`, {
          status: "open",
          userId: user?.id,
          isTransfered: false
        });
        const { data } = await api.get("/tickets/u/" + ticketId);
        setTicket(data);
        setContact(data.contact);
        toast.success("Conversa ativada.");
      } catch (err) {
        toastError(err);
      }
    };

    window.addEventListener("koraxAcceptOrReopenConversation", activateConversation);
    return () => window.removeEventListener("koraxAcceptOrReopenConversation", activateConversation);
  }, [ticket?.id, ticketId, user?.id]);

  const loadFavoriteMessages = useCallback(async () => {
    if (!ticket?.id) return;

    setFavoritesLoading(true);

    try {
      const { data } = await api.get(
        `/messages/${ticket.id}/favorites`
      );

      setFavoriteMessages(data?.messages || []);
    } catch (err) {
      toastError(err);
    } finally {
      setFavoritesLoading(false);
    }
  }, [ticket?.id]);

  useEffect(() => {
    if (!favoritePanelOpen) return;

    loadFavoriteMessages();
  }, [
    favoritePanelOpen,
    favoriteRefreshKey,
    loadFavoriteMessages
  ]);

  useEffect(() => {
    setFavoritePanelOpen(false);
    setFavoriteMessages([]);
    setFavoriteJumpRequest(null);
  }, [ticket?.id]);

  const handleFavoriteMessagesChanged = useCallback(() => {
    setFavoriteRefreshKey(value => value + 1);
  }, []);

  const handleJumpToFavorite = message => {
    if (!message?.id) return;

    setFavoriteJumpRequest({
      messageId: Number(message.id),
      nonce: Date.now()
    });

    setFavoritePanelOpen(false);
  };

  const getFavoriteSender = message => {
    if (message?.fromMe) return "Você";

    return (
      message?.contact?.name ||
      contact?.name ||
      "Cliente"
    );
  };

  const getFavoritePreview = message => {
    const body = String(message?.body || "").trim();

    if (body) return body;

    if (message?.mediaType === "audio") return "🎵 Mensagem de áudio";
    if (message?.mediaType === "image") return "🖼️ Imagem";
    if (message?.mediaType === "video") return "🎬 Vídeo";
    if (message?.mediaType) return "📎 Mídia";

    return "Mensagem";
  };

  useEffect(() => {
    if (!companyId || !socket) return;

    const appointmentEvent =
      `company${companyId}-appointment`;

    const onAppointment = data => {
      const appointmentContactId =
        data?.appointment?.contactId;

      const currentContactId =
        ticket?.contactId ||
        ticket?.contact?.id ||
        contact?.id;

      if (
        data?.action === "delete" ||
        Number(appointmentContactId) ===
          Number(currentContactId)
      ) {
        loadTodayAppointments();
      }
    };

    socket.on(
      appointmentEvent,
      onAppointment
    );

    return () => {
      socket.off(
        appointmentEvent,
        onAppointment
      );
    };
  }, [
    companyId,
    socket,
    ticket?.contactId,
    ticket?.contact?.id,
    contact?.id,
    loadTodayAppointments
  ]);

  useEffect(() => {
    if (!ticket?.id || !companyId || !socket) return;

    const scheduleEvent =
      `company${companyId}-schedule`;

    const onSchedule = data => {
      const scheduleTicketId =
        data?.schedule?.ticketId;

      if (
        data?.action === "delete" ||
        Number(scheduleTicketId) ===
          Number(ticket.id)
      ) {
        loadPendingFollowUps();
      }
    };

    socket.on(scheduleEvent, onSchedule);

    return () => {
      socket.off(scheduleEvent, onSchedule);
    };
  }, [
    ticket?.id,
    companyId,
    socket,
    loadPendingFollowUps
  ]);

  const formatFavoriteDate = date => {
    if (!date) return "";

    try {
      return new Date(date).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "";
    }
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, { [classes.mainWrapperShift]: drawerOpen })}
        style={{ flex: 1, minWidth: 0, border: "none", background: "#FFFFFF" }}
      >
        {/* HEADER ESTILO WHATSAPP */}
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div className={classes.headerRow}>
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={() => setLeadCrmOpen(true)}
                onToggleSearch={handleToggleSearch}
                isSearching={showMessageSearch}
                onOpenKoraxMenu={() => setKoraxActionsOpen(true)}
                onOpenTags={() => setTagsOpen(true)}
                onOpenOpportunity={() => setOpportunityModalOpen(true)}
                onOpenMission={() => setMissionOpen(true)}
                onTransfer={() => setTransferOpen(true)}
              />
            </div>
          )}
          <div className={classes.actionRow}>
            {/* Mensagens favoritas */}
            <button
              type="button"
              className={classes.softActionBtn}
              onClick={() => setFavoritePanelOpen(open => !open)}
              title="Mensagens favoritas"
              aria-label="Mensagens favoritas"
              style={{
                background: favoritePanelOpen ? "#FFF7ED" : "#FFFFFF",
                color: favoritePanelOpen ? "#D97706" : "#64748B"
              }}
            >
              {favoritePanelOpen ? (
                <StarIcon style={{ fontSize: 18 }} />
              ) : (
                <StarBorderIcon style={{ fontSize: 18 }} />
              )}
            </button>

            {/* Etiquetas */}
            <button
              className={`${classes.softActionBtn} ${classes.headerTagsButton}`}
              onClick={() => setTagsOpen(p => !p)}
              title="Etiquetas"
              style={{
                background: tagsOpen ? "#2563EB" : "#FFFFFF",
                color: tagsOpen ? "#FFFFFF" : "#2563EB"
              }}
            >🏷️</button>

            <div className={classes.desktopActions}>
              <TicketActionButtons ticket={ticket} />
            </div>

            <div className={classes.mobileOnly}>
              <TicketActionButtons
                ticket={ticket}
                mobileCompact
              />
            </div>
          </div>
        </TicketHeader>

        {!loading && todayAppointments.length > 0 && (
          <div className={classes.todayAppointmentBanner}>
            <div className={classes.todayAppointmentHeader}>
              <span>📅</span>

              <span>
                {todayAppointments.length === 1
                  ? "Agendamento hoje"
                  : `${todayAppointments.length} agendamentos hoje`}
              </span>
            </div>

            {todayAppointments.map(appointment => {
              const serviceName =
                appointment?.service?.name ||
                "Atendimento";

              const professionalName =
                appointment?.professional?.name ||
                "Profissional";

              const locationName =
                appointment?.location?.name || "";

              const metaText = [
                professionalName,
                locationName
              ]
                .filter(Boolean)
                .join(" · ");

              return (
                <div
                  key={appointment.id}
                  className={classes.todayAppointmentItem}
                >
                  <span
                    className={classes.todayAppointmentTime}
                  >
                    {formatAppointmentTime(
                      appointment.startTime
                    )}
                  </span>

                  <span
                    className={classes.todayAppointmentMain}
                    title={serviceName}
                  >
                    {serviceName}
                  </span>

                  <span
                    className={classes.todayAppointmentMeta}
                    title={metaText}
                  >
                    {metaText}
                  </span>

                  <button
                    type="button"
                    className={
                      classes.todayAppointmentComplete
                    }
                    disabled={
                      Number(finishingAppointmentId) ===
                      Number(appointment.id)
                    }
                    onClick={() =>
                      handleFinishAppointment(
                        appointment
                      )
                    }
                  >
                    {Number(finishingAppointmentId) ===
                    Number(appointment.id)
                      ? "Finalizando..."
                      : "Concluir atendimento"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {!loading && pendingFollowUps.length > 0 && (
          <div className={classes.followUpBanner}>
            <div className={classes.followUpBannerHeader}>
              <span>🕒</span>
              <span>
                {pendingFollowUps.length === 1
                  ? "Follow-up agendado"
                  : `${pendingFollowUps.length} follow-ups agendados`}
              </span>
            </div>

            {pendingFollowUps.map(schedule => (
              <div
                key={schedule.id}
                className={classes.followUpItem}
              >
                <span className={classes.followUpDate}>
                  {formatFollowUpDate(schedule.sendAt)}
                </span>

                <span
                  className={classes.followUpPreview}
                  title={
                    schedule.body ||
                    "Mensagem programada"
                  }
                >
                  {schedule.body ||
                    "Mensagem programada"}
                </span>

                <button
                  type="button"
                  className={classes.followUpCancel}
                  disabled={
                    Number(cancellingFollowUpId) ===
                    Number(schedule.id)
                  }
                  onClick={() =>
                    handleCancelFollowUp(schedule)
                  }
                >
                  {Number(cancellingFollowUpId) ===
                  Number(schedule.id)
                    ? "Cancelando..."
                    : "Cancelar"}
                </button>
              </div>
            ))}
          </div>
        )}

        {favoritePanelOpen && (
          <div className={classes.favoritesPanel}>
            <div className={classes.favoritesPanelHeader}>
              <div className={classes.favoritesPanelTitle}>
                <StarIcon
                  style={{
                    color: "#F59E0B",
                    fontSize: 20
                  }}
                />
                <span>Mensagens favoritas</span>

                {!favoritesLoading && (
                  <span className={classes.favoritesPanelCount}>
                    {favoriteMessages.length}
                  </span>
                )}
              </div>

              <IconButton
                size="small"
                onClick={() => setFavoritePanelOpen(false)}
                aria-label="Fechar favoritas"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>

            <div className={classes.favoritesPanelBody}>
              {favoritesLoading ? (
                <div className={classes.favoriteEmpty}>
                  Carregando favoritas...
                </div>
              ) : favoriteMessages.length === 0 ? (
                <div className={classes.favoriteEmpty}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>☆</div>
                  Nenhuma mensagem favorita nesta conversa.
                </div>
              ) : (
                favoriteMessages.map(message => (
                  <button
                    key={message.id}
                    type="button"
                    className={classes.favoriteMessageItem}
                    onClick={() => handleJumpToFavorite(message)}
                  >
                    <div className={classes.favoriteMessageMeta}>
                      <span className={classes.favoriteMessageSender}>
                        {getFavoriteSender(message)}
                      </span>

                      <span>
                        {formatFavoriteDate(message.createdAt)}
                      </span>
                    </div>

                    <div className={classes.favoriteMessageBody}>
                      {getFavoritePreview(message)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {transferOpen && ticket?.id && (
          <TransferTicketModalCustom
            modalOpen={transferOpen}
            onClose={() => setTransferOpen(false)}
            ticketid={ticket.id}
            ticket={ticket}
          />
        )}

        {ticket?.status === "pending" && (
          <TicketIABar
            mode="ai"
            onAccept={handleAcceptIaConversation}
          />
        )}

        {ticket?.status === "open" && (
          <TicketIABar
            mode="human"
            onResume={handleResumeIaConversation}
          />
        )}


        {/* DROPDOWN ETIQUETAS */}
        {tagsOpen && (
          <div className={classes.tagsDropdown}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0F172A" }}>🏷️ Etiquetas</span>
              <button onClick={() => setTagsOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#94A3B8" }}>✕</button>
            </div>
            <TagsContainer contact={contact} />
          </div>
        )}

        {/* BUSCA */}
        {showMessageSearch && (
          <div className={classes.searchBar}>
            <SearchIcon style={{ color: "#94a3b8", fontSize: 18 }} />
            <InputBase
              autoFocus
              style={{ flex: 1, fontSize: "0.83rem" }}
              placeholder="Buscar mensagens..."
              value={messageSearch}
              onChange={e => setMessageSearch(e.target.value)}
            />
            <IconButton size="small" onClick={handleToggleSearch}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        )}

        {/* MENSAGENS */}
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              <MessagesList
                isGroup={ticket.isGroup}
                onDrop={setDragDropFiles}
                whatsappId={ticket.whatsappId}
                queueId={ticket.queueId}
                channel={ticket.channel}
                ticketDbId={ticket.id}
                ticketUuid={ticket.uuid}
                searchParam={messageSearch}
                favoriteJumpRequest={favoriteJumpRequest}
                onFavoritesChanged={handleFavoriteMessagesChanged}
              />
              <MessageInput
                ticketId={ticket.id}
                ticketStatus={ticket.status}
                ticketChannel={ticket.channel}
                droppedFiles={dragDropFiles}
                contactId={contact.id}
                whatsappId={ticket.whatsappId}
                disableAutoFocus={showMessageSearch}
              />
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>

        {/* SPEED DIAL KORAX */}
        {ticket?.id && (
          <>
            {koraxActionsOpen && (
              <div className={classes.koraxMenuOverlay}>
                <div className={classes.koraxMenuHeader}>
                  <div style={{ fontWeight: 950, fontSize: 15 }}>Assistente Korax</div>
                  <div style={{ fontSize: 12, opacity: .78, marginTop: 3 }}>
                    Ações inteligentes para esta conversa
                  </div>
                </div>

                <button
                  className={classes.koraxMenuItem}
                  onClick={() => {
                    setKoraxActionsOpen(false);
                    setMissionOpen(p => !p);
                  }}
                >
                  <span className={classes.koraxMenuIcon} style={{ background: "rgba(124,58,237,.12)", color: "#7C3AED" }}>🎯</span>
                  <span>
                    <div>Modo Missão</div>
                    <small style={{ color: "#64748B", fontWeight: 600 }}>IA analisa e sugere a próxima ação</small>
                  </span>
                </button>

                <button
                  className={classes.koraxMenuItem}
                  onClick={() => {
                    setKoraxActionsOpen(false);
                    setSeqOpen(true);
                    api.get("/message-sequences")
                      .then(r => setSequences(Array.isArray(r.data) ? r.data : []))
                      .catch(() => {});
                  }}
                >
                  <span className={classes.koraxMenuIcon} style={{ background: "rgba(34,197,94,.12)", color: "#16A34A" }}>⚡</span>
                  <span>
                    <div>Sequências Korax</div>
                    <small style={{ color: "#64748B", fontWeight: 600 }}>Dispare automações comerciais</small>
                  </span>
                </button>

                <button
                  className={classes.koraxMenuItem}
                  onClick={() => {
                    setKoraxActionsOpen(false);
                    setOpportunityModalOpen(true);
                  }}
                >
                  <span className={classes.koraxMenuIcon} style={{ background: "rgba(245,158,11,.14)", color: "#D97706" }}>💰</span>
                  <span>
                    <div>Oportunidade</div>
                    <small style={{ color: "#64748B", fontWeight: 600 }}>Crie ou registre uma venda</small>
                  </span>
                </button>

                <button
                  className={classes.koraxMenuItem}
                  onClick={() => {
                    setKoraxActionsOpen(false);
                    const e = new CustomEvent("openKoraxAssistant");
                    window.dispatchEvent(e);
                  }}
                >
                  <span className={classes.koraxMenuIcon} style={{ background: "rgba(37,99,235,.12)", color: "#2563EB" }}>🤖</span>
                  <span>
                    <div>Korax IA</div>
                    <small style={{ color: "#64748B", fontWeight: 600 }}>Abra o assistente inteligente</small>
                  </span>
                </button>

<button
                  className={classes.koraxMenuItem}
                  onClick={() => {
                    setKoraxActionsOpen(false);
                    setLeadCrmOpen(true);
                  }}
                  style={{ borderBottom: "none" }}
                >
                  <span className={classes.koraxMenuIcon} style={{ background: "rgba(15,23,42,.08)", color: "#0F172A" }}>👤</span>
                  <span>
                    <div>Detalhes do lead</div>
                    <small style={{ color: "#64748B", fontWeight: 600 }}>Contato, tags, notas e kanban</small>
                  </span>
                </button>
              </div>
            )}
</>
        )}

        {/* MODAL SEQUENCIAS */}
        {seqOpen && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => setSeqOpen(false)}>
            <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "70vh", overflow: "auto", boxShadow: "0 -4px 32px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
              <div style={{ width: 40, height: 4, background: "#E2E8F0", borderRadius: 2, margin: "0 auto 16px" }} />
              <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 4 }}>⚡ Iniciar sequência Sequência</div>
              <div style={{ fontSize: "0.78rem", color: "#64748B", marginBottom: 16 }}>Selecione uma sequência para este atendimento.</div>
              {sequences.length === 0 ? (
                <div style={{ color: "#94A3B8", textAlign: "center", padding: "20px 0", fontSize: "0.82rem" }}>Nenhuma sequência cadastrada.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {sequences.filter(s => s.isActive).map(seq => (
                    <div key={seq.id} style={{ background: "#F8FAFC", border: "1px solid #E8EDF5", borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.86rem" }}>{seq.name}</div>
                        {seq.description && <div style={{ fontSize: "0.72rem", color: "#64748B" }}>{seq.description}</div>}
                        <div style={{ fontSize: "0.68rem", color: "#1D4ED8", marginTop: 4 }}>{seq.MessageSequenceSteps?.length || 0} msgs</div>
                      </div>
                      <button
                        disabled={firingSeq === seq.id}
                        onClick={async () => {
                          setFiringSeq(seq.id);
                          try {
                            const realTicketId = ticket?.id || ticketId;

                            if (!realTicketId) {
                              toast.error("Erro: conversa não identificada.");
                              setFiringSeq(null);
                              return;
                            }

                            const r = await api.post("/message-sequences/fire", {
                              sequenceId: seq.id,
                              ticketId: realTicketId
                            });

                            if (r.data.success) {
                              toast.success("✅ " + r.data.message);
                              setSeqOpen(false);
                            } else {
                              toast.error("Erro: " + (r.data.error || "falha desconhecida"));
                            }
                          } catch (err) {
                            const msg =
                              err?.response?.data?.error ||
                              err?.response?.data?.message ||
                              err?.message ||
                              "Erro ao disparar";
                            console.error("Erro ao disparar sequência:", err?.response || err);
                            toast.error(msg);
                          }
                          setFiringSeq(null);
                        }}
                        style={{ background: "#1D4ED8", color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}
                      >{firingSeq === seq.id ? "..." : "▶ Iniciar sequência"}</button>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setSeqOpen(false)} style={{ marginTop: 16, width: "100%", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, color: "#64748B" }}>Fechar</button>
            </div>
          </div>
        )}

        <OpportunityModal
          open={opportunityModalOpen}
          onClose={() => setOpportunityModalOpen(false)}
          ticketId={ticket?.id}
          contact={contact}
          onSaved={() => setOpportunityModalOpen(false)}
        />
      </Paper>

      {missionOpen && (
        <div className={classes.missionDrawer}>
          <ModoMissao
            ticket={ticket}
            messages={ticketMessages}
            onClose={() => setMissionOpen(false)}
            onUseMessage={msg => {
              const el = document.querySelector('[data-testid="message-input"]') || document.querySelector("textarea");
              if (el) { el.focus(); document.execCommand("insertText", false, msg); }
            }}
            onCreateFollowup={() => {}}
          />
        </div>
      )}

      <ConversationLeadCRM
        open={leadCrmOpen}
        onClose={() => setLeadCrmOpen(false)}
        ticket={ticket}
        contact={contact}
        onCreateOpportunity={() => {
          setLeadCrmOpen(false);
          setOpportunityModalOpen(true);
        }}
        onOpenContactTools={() => {
          setLeadCrmOpen(false);
          setDrawerOpen(true);
        }}
      />

      <ContactOverviewDrawer
        open={drawerOpen}
        contactId={contact?.id || ticket?.contactId}
        onClose={() => setDrawerOpen(false)}
        onEdit={() => {
          setDrawerOpen(false);
          setLegacyContactDrawerOpen(true);
        }}
      />

      {/* Edição antiga preservada temporariamente */}
      <ContactDrawer
        open={legacyContactDrawerOpen}
        handleDrawerClose={() => setLegacyContactDrawerOpen(false)}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />
    </div>
  );
};

export default Ticket;
